import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SimplePagination, Table } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { useMount } from 'react-use';
import { numbersApi } from 'apis/numbers';
import { ratingApi } from 'apis/rating';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { SelectSurveyModal } from '../../../survey/components/languages/components/select/selectSurveyModal';
import { PhoneContactPointHeadRow } from './components/phoneSurveysTableHeadRow/phoneSurveysTableHeadRow';
import { PhoneSurveyTableRow } from './components/phoneSurveysTableRow/phoneSurveysTableRow';
/**
 * Represents a component that displays a table with the phone numbers with
 * survey links.
 */
export const PhoneSurveysTable = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [readRatingSettings, { data: readRatingSettingsLinksResponse, isSuccess: readRatingSettingsLinksIsSuccess, },] = ratingApi.useLazyReadRatingSettingsLinksQuery();
    const [phoneSurveyLinks, setPhoneSurveyLiks] = useState([]);
    const { items: phoneNumbers, asyncStatus: phoneNumbersAsyncStatus, hasNextPage, hasPreviousPage, nextPage, previousPage, } = usePaginatedEndpoint({
        endpoint: numbersApi.endpoints.readNumbers,
        initialArg: { limit: 12 },
        itemsSelector: ({ data }) => data.numbers,
    });
    // Finishes initializing after the data has been fetched.
    useEffect(() => {
        var _a;
        if (phoneNumbersAsyncStatus === 'fulfilled' &&
            phoneNumbers.length &&
            readRatingSettingsLinksIsSuccess) {
            setRatingSettingsLinks((_a = readRatingSettingsLinksResponse === null || readRatingSettingsLinksResponse === void 0 ? void 0 : readRatingSettingsLinksResponse.data.rating_settings_links) !== null && _a !== void 0 ? _a : []);
            setIsLoading(false);
        }
    }, [
        phoneNumbers,
        phoneNumbersAsyncStatus,
        readRatingSettingsLinksResponse,
        readRatingSettingsLinksIsSuccess,
    ]);
    const [ratingSettingsLinks, setRatingSettingsLinks] = useState([]);
    useMount(() => {
        readRatingSettings({ contactPointKind: 'phone_number' });
    });
    // Creates the table data items whenever the constituent parts are ready. The
    // queues can come back with empty array, so we don't require that to be
    // present.
    useEffect(() => {
        if (phoneNumbers.length) {
            setPhoneSurveyLiks(phoneNumbers.reduce((phoneSurveysLinks, phoneNumber) => [
                ...phoneSurveysLinks,
                {
                    phoneNumber: phoneNumber,
                    link: ratingSettingsLinks
                        .filter((link) => link.contact_point === phoneNumber.e164_format)
                        .shift(),
                },
            ], []));
        }
    }, [phoneNumbers, ratingSettingsLinks, readRatingSettingsLinksResponse]);
    /**
     * Handles whenever an item is changed in order to update the survey links
     * table to reflect the changes.
     */
    const handleLinkTableDataItemChanged = useCallback((updatedTableDataItem) => {
        setPhoneSurveyLiks((tableData) => tableData.map((tableDataItem) => tableDataItem.phoneNumber.e164_format ===
            updatedTableDataItem.phoneNumber.e164_format
            ? updatedTableDataItem
            : tableDataItem));
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs(Table, Object.assign({ loading: isLoading, loadingStrategy: "disabled", stickyHeader: true }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Number" }), _jsx(Table.Th, { children: "Name" }), _jsx(Table.Th, { children: "Survey" }), _jsx(Table.Th, Object.assign({ width: "0" }, { children: "Enabled" })), _jsx(Table.Th, { width: "0" })] }) }), _jsx(Table.Tbody, { children: isLoading ? (_jsx(PhoneContactPointHeadRow, {})) : (phoneSurveyLinks.map((phoneSurveyLink) => (_jsx(PhoneSurveyTableRow, { phoneSurveyLink: phoneSurveyLink, onItemChanged: handleLinkTableDataItemChanged }, phoneSurveyLink.phoneNumber.e164_format)))) }), _jsx(Table.Footer, { children: _jsx(SimplePagination, { nextButtonClick: nextPage, nextButtonDisabled: !hasNextPage, previousButtonClick: previousPage, previousButtonDisabled: !hasPreviousPage }) })] })), _jsx(SelectSurveyModal, {})] }));
};
